<template>
  <div class="w-full">
    <div class="w-full px-6 h-20 flex justify-center items-center border-b relative">
      <h4 class="text-yank">All Media <span class="text-payneHot">({{ mediaCounts.all }})</span></h4>
      <div v-if="! $page.props.user" class="absolute right-0 inset-y-0 hidden lg:flex items-center mr-10">
        <AuthButtons />
      </div>
    </div>
    <section class="p-8 lg:px-16 lg:pt-16 lg:pb-20 2xl:pt-20 2xl:pb-32 bg-yank text-white relative flex flex-col">
      <div class="lg:max-w-sm 2xl:max-w-none order-1 relative z-10">
        <h1 class="text-3xl font-bold mb-3">{{ settings.hero_title }}</h1>
        <p class="text-payneMiddle mb-8">{{ settings.hero_subtitle }}</p>
        <SearchInput @search="runSearch" input-classes="text-white transparent-bg w-full md:max-w-sm"/>
      </div>
      <img
        class="w-full  h-40 object-cover object-center lg:absolute lg:right-0 lg:top-10 -z-10 lg:mr-10 order-0 mb-10 lg:mb-0 lg:w-auto lg:h-auto lg:max-w-xs xl:max-w-none"
        :src="settings.hero_image"
        alt="Tithely Media"
      />
      <div class="absolute inset-0 -z-20">
        <BgGradients class="w-auto h-full"/>
      </div>
    </section>
    <!--Content-->
    <div class="px-10 py-12 grid grid-cols-1 gap-y-16 w-full">
      <!--New and Noteworthy-->
      <CarouselList v-if="featuredMedia.length" carousel-title="New and Noteworthy" :items="featuredMedia"/>
      <!--Popular This Month-->
      <CarouselList v-if="recentMedia.length" carousel-title="Popular This Month" :items="recentMedia"/>
      <!--Catergories-->
      <section>
        <h4 class="font-bold text-2xl mb-4">Categories</h4>
        <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-4 gap-8">
          <CategoryCard v-for="category in categories" :category="category" :key="category.mediaType"/>
        </div>
      </section>
      <!--All media-->
      <section class="w-full">
        <h4 class="font-bold text-2xl mb-4">All media</h4>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2">
          <ItemComponent v-for="item in allMedia" :item="item" :key="item.id" height="h-48" imageWidth="w-full" videoWidth="w-full" class="p-2"/>
        </div>
        <!-- Custom Infinite Loading -->
        <div v-if="isLoading" class="items-grid mt-8">
          <item-skeleton v-for="item in 10" :key="`skeleton-item-${item}`"/>
        </div>
        <div v-else-if="!hasMorePages" class="mt-8 text-sm font-medium text-payneFire text-center">
          No more media to display
        </div>
        <div ref="loadTrigger" class="h-4"></div>
      </section>
    </div>
  </div>
</template>

<script>
import AppLayout from '@/layout/app-layout'
import AuthButtons from "@/components/shared/AuthButtons"
import SearchInput from "@/components/shared/SearchInput"
import CategoryCard from "@/components/home/CategoryCard"
import CarouselList from "@/components/shared/CarouselList"
import ItemComponent from "@/components/home/CarouselItemComponent"
import BgGradients from "@/components/svg/bgGradients"
import ItemSkeleton from "@/components/shared/ItemSkeleton"


export default {
  name: 'Home',
  layout: AppLayout,

  props: {
    mediaCounts: Object,
    recentMedia: Array,
    featuredMedia: Array,
    media: Object,
    categories: Array,
    settings: Object,
  },

  components: {AuthButtons, ItemComponent, CarouselList, CategoryCard, SearchInput, BgGradients, ItemSkeleton},

  remember: ['allMedia'],

  data() {
    return {
      allMedia: this.media.data,
      isLoading: false,
      intersectionObserver: null,
    }
  },

  computed: {
    hasMorePages() {
      return this.media.links && this.media.links.next
    }
  },

  watch: {
    media(media) {
      this.allMedia.push(...media.data)
    }
  },

  methods: {
    runSearch(query) {
      this.$inertia.visit(this.$route('search', {'filter[search]': query}), {
        only: ['media', 'query'],
      })
    },

    loadMore() {
      if (!this.hasMorePages || this.isLoading) {
        return
      }

      this.isLoading = true

      this.$inertia.visit(this.media.links.next, {
        preserveState: true,
        preserveScroll: true,
        only: ['media'],
        onFinish: () => {
          this.isLoading = false
        },
        onError: () => {
          this.isLoading = false
        }
      })
    },

    setupIntersectionObserver() {
      if (!this.$refs.loadTrigger) return

      const options = {
        root: null,
        rootMargin: '100px',
        threshold: 0
      }

      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && this.hasMorePages && !this.isLoading) {
            this.loadMore()
          }
        })
      }, options)

      this.intersectionObserver.observe(this.$refs.loadTrigger)
    },

    destroyIntersectionObserver() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect()
        this.intersectionObserver = null
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.setupIntersectionObserver()
    })
  },

  beforeDestroy() {
    this.destroyIntersectionObserver()
  }
}
</script>
